<script lang="ts">
    import { InfoIcon } from "@/Common";
    import { cn } from "@/lib/utils";
    import type { MyFitnessStores } from "@/MyFitness/stores";
    import { convertDisplayDistance, getDisplayAltitude, uniqueId } from "@/Util/utils";
    import { getContext } from "svelte";

    export let altitudePoints: number[]
    export let kge: number
    export let distance: number
    export let elevation: number
    export let width = 175
    export let height = 50
    let className: string = undefined
    export {className as class}    

    const { userParams } = getContext<MyFitnessStores>('myFitness')
    let altitudeMin: number
    let altitudeMax: number
    let polylineAltitudeFillPoints: string
    if (altitudePoints) {
        altitudeMax = Math.max(...altitudePoints)
        altitudeMin = Math.min(...altitudePoints)
        const yRatio = height / altitudeMax
        const xRatio = width / altitudePoints.length;
        
        // Scale the altitude points into the correct size
        (() => {
            let verticalShift = Math.min(altitudeMin, 0)
            let points = altitudePoints.map((value, index) => {
                const scaledX = index === altitudePoints.length-1 ? width : index*xRatio
                const scaledY = height - ((value - verticalShift)*yRatio)

                return `${scaledX},${scaledY}`
            })
            points.unshift(`0,${height}`)
            points.push(`${width},${height}`)
            polylineAltitudeFillPoints = points.join(' ')
        })()
    }
    
    const gradientId = uniqueId('altGradient-')
    function getGradientStopColor(altitude: number) {
        switch (true) {
            case (altitude < 100): return "text-xerteal-200"
            case (altitude < 200): return "text-xerteal-400"
            case (altitude < 300): return "text-xerteal-600"
            case (altitude < 400): return "text-xerteal-800"
            case (altitude < 500): return "text-xerteal-900"
            default: "text-xerteal-950"
        }
    }
</script>

<div class={cn("twp relative", className)}>
    {#if altitudePoints}
        <svg class="rounded" viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id={gradientId} x1="0%" x2="0%" y1="100%" y2="0%"> 
                    <stop class="text-xerteal-50" offset="0%" stop-color="currentColor" />
                    <stop class={getGradientStopColor(altitudeMin)} offset="{(altitudeMin / altitudeMax)*100}%" stop-color="currentColor" />
                    <stop class={getGradientStopColor(altitudeMax)} offset="100%" stop-color="currentColor"/>
                </linearGradient>
            </defs>
            <g class="origin-center">
                <polyline class="opacity-30" points={polylineAltitudeFillPoints} fill="url(#{gradientId})"/>
            </g>       
        </svg>
    {/if}

    <div class="absolute inset-0 grid grid-cols-3 items-center">
        <div class="space-y-1 text-center">
            <div class="leading-none font-bold text-2xl text-xerteal-800">{convertDisplayDistance(distance, $userParams.activityUnits.dist)}<span class="text-sm">{$userParams.activityUnits.dist}</span></div>
            <div class="leading-none medium text-lg text-xerteal-700">Distance</div>
        </div>
        <div class="space-y-1 text-center">
            <div class="leading-none font-bold text-2xl text-xerteal-800">{getDisplayAltitude(elevation, $userParams.activityUnits.alt)}<span class="text-sm">{$userParams.activityUnits.alt}</span></div>
            <div class="leading-none medium text-lg text-xerteal-700">Elevation</div>
        </div>
        {#if kge}
            <div class="flex flex-col items-center gap-1 text-center">
                <div class="leading-none font-bold text-2xl text-xerteal-800">{kge.toFixed(2)}</div>
                <div class="leading-none medium text-lg text-xerteal-700">KGE <InfoIcon href="https://baronbiosys.com/glossary/kilogram-exponent" /></div>
            </div>
        {/if}
    </div>
</div>
    
